import { ChangeDetectorRef, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ApiSettings, LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { SentEmailListTableComponent } from '../../components/sent-email-list-table/sent-email-list-table.component';
import { SentEmailSearchFilterComponent } from '../../components/sent-email-search-filter/sent-email-search-filter.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
  selector: 'ms-sent-email-list-page',
  imports: [
    PageTitleComponent,
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    SentEmailListTableComponent,
    SentEmailSearchFilterComponent,
    SortDropdownComponent
  ],
  templateUrl: './sent-email-list.page.html',
  styleUrl: './sent-email-list.page.scss'
})
export class SentEmailListPage implements OnInit {

  public sortOptions = ListConstants.SENT_EMAILS_SORT_OPTIONS();
  public orderBy = signal<string>(ListConstants.SENT_EMAILS_ORDERBY_DEFAULT());

  @ViewChild('sentEmailTable') public sentEmailTable?: SentEmailListTableComponent;

  public userSessionService = inject(UserSessionService);
  private changeDetector = inject(ChangeDetectorRef);

  public async ngOnInit(): Promise<void> {
    await this.scopeFilterToActiveOrganization();
    this.orderBy.set(this.userSessionService.sentEmailsOrderBy);
  }

  public onSortChanged(orderBy: string): void {
    if (!orderBy || orderBy === this.orderBy()) return;
    this.orderBy.set(orderBy);
    this.userSessionService.sentEmailsOrderBy = orderBy;
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.SENT_EMAILS_ORDERBY(),
      orderBy
    );
    this.sentEmailTable?.setSortOrder(orderBy);
  }

  public onListOrderByChanged(orderBy: string): void {
    if (!orderBy || orderBy === this.orderBy()) return;
    this.orderBy.set(orderBy);
    this.userSessionService.sentEmailsOrderBy = orderBy;
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.SENT_EMAILS_ORDERBY(),
      orderBy
    );
  }

  public async onFilterApplied(): Promise<void> {
    this.persistFilter();
    await this.sentEmailTable?.loadSentEmails(false);
    this.changeDetector.detectChanges();
  }

  public async clearSearch(): Promise<void> {
    this.userSessionService.sentEmailsFilter.searchTerm = '';
    await this.onFilterApplied();
  }

  private async scopeFilterToActiveOrganization(): Promise<void> {
    const activeOrg = this.userSessionService.activeOrganization();
    if (activeOrg) {
      this.userSessionService.sentEmailsFilter.organizationIds = [activeOrg.organizationId];
    }
    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.userSessionService.sentEmailsFilter.timeZoneId =
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;
  }

  private persistFilter(): void {
    this.userSessionService.saveToLocalStorage(
      LocalStorageKeyConstants.SENT_EMAILS_FILTER(),
      this.userSessionService.sentEmailsFilter
    );
  }
}
